/**
 * Lazy canvas page loading helpers.
 * canvasAllData remains the assembled { [pageId]: Component[] } shape;
 * under single-page memory mode only keepPages hold full arrays, others are Back skeletons.
 */

import { cancelCanvasDraftWrites } from "./canvasDraftWriter";
import { stringifyComponentIds } from "./componentId";

function normalizeCanvasIdFields(data) {
  if (!data || typeof data !== "object") {
    return data;
  }
  Object.keys(data).forEach((pageId) => {
    const pageArr = data[pageId];
    if (Array.isArray(pageArr)) {
      stringifyComponentIds(pageArr);
    }
  });
  return data;
}

export function parseCanvasAllData(canvasAllData) {
  if (!canvasAllData) {
    return {};
  }
  if (typeof canvasAllData === "object") {
    return normalizeCanvasIdFields(canvasAllData);
  }
  try {
    const parsed = JSON.parse(canvasAllData) || {};
    return normalizeCanvasIdFields(parsed);
  } catch (e) {
    return {};
  }
}

export function stringifyCanvasAllData(data) {
  return JSON.stringify(data || {});
}

/** Mark Back component as lazy-load eviction skeleton (not user-cleared empty page). */
export function markLazySkeletonBack(backComp) {
  if (!backComp || typeof backComp !== "object") {
    return backComp;
  }
  return { ...backComp, _lazySkeleton: true };
}

/** Whether a page array is a lazy-load eviction skeleton (must not overwrite DB). */
export function isPageSkeleton(pageArr) {
  if (!Array.isArray(pageArr) || pageArr.length === 0) {
    return true;
  }
  if (pageArr.length > 1) {
    return false;
  }
  const only = pageArr[0];
  if (!only) {
    return true;
  }
  if (only._lazySkeleton === true) {
    return true;
  }
  return false;
}

/** Remove lazy-load markers before persisting to server. */
export function stripLazySkeletonFromPage(pageArr) {
  if (!Array.isArray(pageArr)) {
    return pageArr;
  }
  return pageArr.map((comp) => {
    if (!comp || typeof comp !== "object") {
      return comp;
    }
    if (!Object.prototype.hasOwnProperty.call(comp, "_lazySkeleton")) {
      return comp;
    }
    const copy = { ...comp };
    delete copy._lazySkeleton;
    return copy;
  });
}

/**
 * Normalize findPage response pageData to component array.
 */
export function normalizePageDataPayload(pageData) {
  if (pageData == null) {
    return [];
  }
  if (Array.isArray(pageData)) {
    return pageData;
  }
  if (typeof pageData === "string") {
    try {
      const parsed = JSON.parse(pageData);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
}

/**
 * Merge one page into canvasAllData string; returns new string.
 */
export function mergePageIntoCanvasAllData(canvasAllData, pageId, pageArr) {
  const data = parseCanvasAllData(canvasAllData);
  data[String(pageId)] = pageArr;
  return stringifyCanvasAllData(data);
}

/**
 * Flush current editor page into canvasAllData.
 */
export function flushCurrentPage(canvasAllData, pageId, componentData) {
  if (pageId === undefined || pageId === null) {
    return canvasAllData;
  }
  return mergePageIntoCanvasAllData(
    canvasAllData,
    pageId,
    componentData || []
  );
}

/**
 * Collect page ids that need remote load.
 */
export function collectUnloadPageIds(canvasAllData, pageIds, loadedSet) {
  const data = parseCanvasAllData(canvasAllData);
  const need = [];
  (pageIds || []).forEach((id) => {
    const key = String(id);
    if (loadedSet && loadedSet.has(key)) {
      return;
    }
    if (isPageSkeleton(data[key])) {
      need.push(key);
    }
  });
  return need;
}

/**
 * Ask VisualDrag to load pages via eventBus. Resolves when done (or immediately if none).
 */
export function ensureCanvasPagesAsync(eventBus, pageIds) {
  return new Promise((resolve) => {
    if (!eventBus || !pageIds || !pageIds.length) {
      resolve();
      return;
    }
    eventBus.$emit("ensureCanvasPages", {
      pageIds,
      done: resolve,
    });
  });
}

/** Collect all page ids from pageOptions tree. */
export function collectPageOptionIds(pageOptions) {
  const ids = [];
  (pageOptions || []).forEach((p) => {
    ids.push(p.value);
    if (p.children && p.children.length) {
      p.children.forEach((c) => ids.push(c.value));
    }
  });
  return ids;
}

/** Extract Back-only skeleton from a page array (for memory eviction). */
export function extractBackSkeleton(pageArr) {
  if (!Array.isArray(pageArr) || !pageArr.length) {
    return [];
  }
  const back = pageArr.find(
    (c) => c && (c.id === "Back" || c.component === "Back")
  );
  if (!back) {
    return [];
  }
  return [markLazySkeletonBack(JSON.parse(JSON.stringify(back)))];
}

/**
 * Keep only specified page ids as full data; other pages shrink to Back skeleton.
 * Returns { canvasAllData, keptIds }.
 */
export function compactCanvasMemory(canvasAllData, keepPageIds) {
  const data = parseCanvasAllData(canvasAllData);
  const keep = new Set((keepPageIds || []).map((id) => String(id)));
  const next = {};
  Object.keys(data).forEach((pageId) => {
    const arr = data[pageId];
    if (keep.has(String(pageId))) {
      next[pageId] = arr;
    } else {
      next[pageId] = extractBackSkeleton(arr);
    }
  });
  return {
    canvasAllData: stringifyCanvasAllData(next),
    keptIds: Array.from(keep),
  };
}

/** Collect ChildPage bindPage ids from a page component array. */
export function collectBoundChildPageIds(pageArr) {
  const ids = [];
  if (!Array.isArray(pageArr)) {
    return ids;
  }
  pageArr.forEach((comp) => {
    if (
      comp &&
      comp.component === "ChildPage" &&
      comp.style &&
      comp.style.bindPage &&
      comp.style.bindPage.value != null
    ) {
      ids.push(String(comp.style.bindPage.value));
    }
  });
  return ids;
}

/** 离开画布或不保存返回时清理浏览器侧草稿，避免新建工程读到上一份模板 */
export function clearCanvasSessionStorage() {
  sessionStorage.removeItem("pageSortData");
  sessionStorage.removeItem("canvasErrorMap");
  sessionStorage.removeItem("canvasStyle");
  sessionStorage.removeItem("interactionPackageData");
}

/** 保存返回 / 不保存返回 / 离开画布：清本地状态，不影响下一次操作 */
export function leaveCanvasEditor(store) {
  if (!store) {
    return;
  }
  cancelCanvasDraftWrites();
  store.commit("setCanvasEditorLeaving", true);
  clearCanvasSessionStorage();
  store.commit("removeCache");
}
